/**
 * THE F-16 NOTICE'S RULE (Prest0n Everywhere S1c-1b-2; Miles, 2026-10-22: "prompt once").
 *
 * A number confirmed only by a YES reply to a text will soon stop counting as proved (S1c-2). Its holder is told once,
 * in the banner band, and pointed to Settings → "Phones & email", where the line beside the add-a-phone form names the
 * number and the code confirms it. Pure, so the rule is tested without Firebase; ProvePhoneBanner.tsx is the wiring and
 * provePhoneCopy.ts the words.
 *
 * On the first download (the band's sources are), so it imports nothing but types and the section's address (a module
 * of one constant that imports nothing).
 */
import type { SmsConsentStatus } from '../../services/smsVerificationClient';
import { PHONES_AND_EMAIL_HASH } from '../../lib/identity/phonesAndEmailAddress';

/** What the band knows before the list door is asked anything. */
export interface ProvePhoneFacts {
  /** The signed-in person's id; null when nobody is, or the session is anonymous. */
  uid: string | null;
  /** The client lever (accountSecurityShows) admits the tab's workspace. */
  leverAdmits: boolean;
  /** The SMS consent record's state: only a confirmed number can be one a YES reply confirmed. */
  consent: SmsConsentStatus;
  /** This person has seen the notice already (dismissed it, or been to Settings since it applied). */
  seen: boolean;
}

/** Whether the list door is asked at all: only once everything the band already knows says the notice could show. */
export function provePhoneWorthReading(f: ProvePhoneFacts): boolean {
  return !!f.uid && f.leverAdmits && f.consent === 'confirmed' && !f.seen;
}

/**
 * Whether the notice shows: the list door named the number (its `provePhone`), the band is on the app's pages, and the
 * page is not Settings, which says it beside the add-a-phone form.
 */
export function provePhoneShows(
  f: ProvePhoneFacts & { provePhone: { display: string } | null; onSettings: boolean; onAppSurface: boolean },
): boolean {
  return provePhoneWorthReading(f) && !!f.provePhone && f.onAppSurface && !f.onSettings;
}

/** The Settings page, by its address alone. */
export const SETTINGS_PATH = '/settings';

/**
 * Where the notice's action leads: Settings, at its Phones & email section, which the page lands on with the
 * add-a-phone field focused once the section shows (S1c-1b-3; the owner, 2026-10-23: the action "did not take them to
 * the right place on the Settings page to confirm the number").
 */
export const PROVE_PHONE_TO = `${SETTINGS_PATH}${PHONES_AND_EMAIL_HASH}`;

/**
 * Whether a page is that Settings page, whichever tree it is in (the caller passes the app path): its address alone,
 * as the router matches it (any case, a trailing slash), whatever its hash. Team & roles (/settings/team) and Skills
 * (/settings/skills) are pages of their own that say nothing about the number (review round 1, M1).
 */
export function isSettingsPath(appPath: string): boolean {
  return appPath.toLowerCase().replace(/\/+$/, '') === SETTINGS_PATH;
}

/** FNV-1a (32 bits, hex): one browser's people told apart, with nothing to read back. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** Where the person's "seen" mark is kept: one key per person, named by a hash of their id, never the id itself. */
export const PROVE_PHONE_SEEN_PREFIX = 'prest0n_provephone_seen:';

export function provePhoneSeenKey(uid: string): string {
  return PROVE_PHONE_SEEN_PREFIX + fnv1a(uid);
}

type SeenStore = Pick<Storage, 'getItem' | 'setItem'>;

/** Whether this person has seen the notice. Storage that cannot be read counts as not seen. */
export function readProvePhoneSeen(uid: string, store: SeenStore | null): boolean {
  try {
    return store?.getItem(provePhoneSeenKey(uid)) === '1';
  } catch {
    return false;
  }
}

/** Marks the notice seen for good, for this person (never by the daily rule other notices use). */
export function writeProvePhoneSeen(uid: string, store: SeenStore | null): void {
  try {
    store?.setItem(provePhoneSeenKey(uid), '1');
  } catch {
    /* storage refused: the notice may show once more */
  }
}

/**
 * A list that named no number is not asked again for a day, per person, workspace and confirmation (review round 1,
 * L6; round 2, N1). The door names none where the workspace may not prove phones yet, or once a code has proved the
 * number, and both last; asked on every page load, the read would go on for good for people the notice can never
 * reach. A day lets a workspace's phones being switched on reach them by the next day; a number confirmed by YES since
 * (the consent record's confirmation, by the server's clock) is a question of its own at once. A read that failed, was
 * refused, or whose consent record the server could not read this time (`provePhoneUnread`) is not remembered.
 */
export const PROVE_PHONE_QUIET_MS = 24 * 60 * 60 * 1000;
export const PROVE_PHONE_QUIET_PREFIX = 'prest0n_provephone_quiet:';

/** Whose question it is: the person, the tab's workspace, and when the consent record's YES confirmed (null: unknown). */
export interface ProvePhoneQuestion {
  uid: string;
  accountId: string;
  confirmedAtMs: number | null;
}

export function provePhoneQuietKey(q: ProvePhoneQuestion): string {
  return PROVE_PHONE_QUIET_PREFIX + fnv1a(`${q.uid}:${q.accountId}:${q.confirmedAtMs ?? ''}`);
}

/** Whether the list named no number for this question within the last day (by this device's clock). */
export function readProvePhoneQuiet(q: ProvePhoneQuestion, store: SeenStore | null, nowMs: number): boolean {
  try {
    const at = Number(store?.getItem(provePhoneQuietKey(q)));
    return Number.isFinite(at) && at > 0 && at <= nowMs && nowMs - at < PROVE_PHONE_QUIET_MS;
  } catch {
    return false;
  }
}

export function writeProvePhoneQuiet(q: ProvePhoneQuestion, store: SeenStore | null, nowMs: number): void {
  try {
    store?.setItem(provePhoneQuietKey(q), String(nowMs));
  } catch {
    /* storage refused: the list is asked again on the next page load */
  }
}
